import React, { useState, useEffect, useCallback } from 'react';
import {
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Alert,
  Stack,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  SelectChangeEvent,
} from '@mui/material';
import AppDialog from '../common/AppDialog';
import { Remove as RemoveIcon } from '@mui/icons-material';
import { Item, RemoveStockRequest, InventoryBatch } from '../../types';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { errorMessage } from '../../utils/errorMessage';
import { tracksBatches } from '../../utils/inventoryMode';
import { formatCurrency } from '../../utils/currency';

interface RemoveStockDialogProps {
  open: boolean;
  onClose: () => void;
  item: Item;
  onSuccess: () => void | Promise<void>;
  batchTrackingEnabled?: boolean;
}

const RemoveStockDialog: React.FC<RemoveStockDialogProps> = ({
  open,
  onClose,
  item,
  onSuccess,
  batchTrackingEnabled,
}) => {
  const { t } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [batches, setBatches] = useState<InventoryBatch[]>([]);
  const [batchesLoading, setBatchesLoading] = useState(false);
  const canUseBatchTracking = batchTrackingEnabled !== false;

  const [formData, setFormData] = useState<RemoveStockRequest>({
    quantity: 1,
    notes: '',
    batchId: undefined,
  });

  const loadBatches = useCallback(async () => {
    if (!canUseBatchTracking || !item) return;
    
    setBatchesLoading(true);
    try {
      const batchesData = await apiClient.getInventoryBatches(item.id);
      // Only show batches with remaining quantity > 0
      setBatches(batchesData.filter(batch => batch.remainingQuantity > 0));
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to load batches'));
    } finally {
      setBatchesLoading(false);
    }
  }, [canUseBatchTracking, item]);

  // Load batches when dialog opens and item has batch tracking
  useEffect(() => {
    if (open && item && canUseBatchTracking && tracksBatches(item)) {
      loadBatches();
    }
  }, [canUseBatchTracking, open, item, loadBatches]);

  useEffect(() => {
    if (open) {
      setFormData({ quantity: 1, notes: '', batchId: undefined });
      setError(null);
    }
  }, [open, item.id]);

  const handleInputChange = (field: keyof RemoveStockRequest) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setFormData(prev => ({
      ...prev,
      [field]: field === 'quantity' ? (value === '' ? '' : Number(value)) : value,
    }));
  };

  const handleBatchChange = (event: SelectChangeEvent<string>) => {
    const batchId = event.target.value;
    setFormData(prev => ({
      ...prev,
      batchId: batchId || undefined,
      quantity: 1, // Reset quantity when batch changes
    }));
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);
    setError(null);

    if (!Number.isInteger(Number(formData.quantity)) || Number(formData.quantity) <= 0 || Number(formData.quantity) > maxQuantity) {
      setError(t('inventory.quantityToRemove'));
      setLoading(false);
      return;
    }

    try {
      const request: RemoveStockRequest = {
        quantity: Number(formData.quantity),
        notes: formData.notes || undefined,
        batchId: formData.batchId,
      };

      const response = await apiClient.removeStock(item.id, request);
      
      if (response.success) {
        await onSuccess();
        onClose();
        setFormData({ quantity: 1, notes: '', batchId: undefined });
      } else {
        setError(response.message);
      }
    } catch (err: unknown) {
      setError(errorMessage(err, 'Failed to remove stock'));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
      setError(null);
    }
  };

  // Get selected batch for validation
  const selectedBatch = formData.batchId ? batches.find(b => b.id === formData.batchId) : null;
  
  // Validation logic
  const maxQuantity = canUseBatchTracking && tracksBatches(item) && selectedBatch
    ? selectedBatch.remainingQuantity 
    : item.totalStockQuantity;
    
  const isFormValid = Number.isInteger(Number(formData.quantity)) && Number(formData.quantity) > 0 && Number(formData.quantity) <= maxQuantity;
  const newQuantity = item.totalStockQuantity - Number(formData.quantity);

  return (
    <AppDialog presentation="compact" open={open} onClose={handleClose} closeDisabled={loading} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit}>
        <DialogTitle>{t('inventory.removeStock')}</DialogTitle>

        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>{item.name}</Typography>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <Stack spacing={2}>
            {/* Batch Selection for batch-tracked items */}
            {canUseBatchTracking && tracksBatches(item) && (
              <FormControl fullWidth>
                <InputLabel>{t('inventory.selectBatch')}</InputLabel>
                <Select
                  value={formData.batchId || ''}
                  onChange={handleBatchChange}
                  label={t('inventory.selectBatch')}
                  disabled={batchesLoading}
                >
                  <MenuItem value="">
                    <em>{t('inventory.autoSelectFIFO')}</em>
                  </MenuItem>
                  {batches.map((batch) => (
                    <MenuItem key={batch.id} value={batch.id}>
                      <Box display="flex" alignItems="center" gap={1} width="100%">
                        <Typography variant="body2">
                          {batch.batchNumber || t('inventory.noBatchNumber')}
                        </Typography>
                        <Chip 
                          size="small" 
                          label={`${batch.remainingQuantity} ${t('inventory.units')}`}
                          color={batch.expired ? 'error' : batch.expiringSoon ? 'warning' : 'default'}
                        />
                        {(batch.priceGross ?? batch.price) && (
                          <Typography variant="caption" color="text.secondary">
                            {formatCurrency(batch.priceGross ?? batch.price)}
                          </Typography>
                        )}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                {batchesLoading && (
                  <Typography variant="caption" color="text.secondary">
                    {t('common.loading')}
                  </Typography>
                )}
              </FormControl>
            )}

            <TextField
              label={t('inventory.quantityToRemove')}
              type="number"
              value={formData.quantity}
              onChange={handleInputChange('quantity')}
              fullWidth
              required
              inputProps={{ min: 1, max: maxQuantity, step: 1 }}
              InputProps={{
                endAdornment: <InputAdornment position="end">{t('inventory.units')}</InputAdornment>,
              }}
              helperText={
                selectedBatch 
                  ? `${t('inventory.available')} ${t('inventory.inBatch')}: ${selectedBatch.remainingQuantity} ${t('inventory.units')}`
                  : `${t('inventory.available')}: ${item.totalStockQuantity} ${t('inventory.units')}`
              }
              sx={{ '& .MuiInputLabel-root': { fontSize: { xs: '0.875rem', sm: '1rem' } } }}
            />

            <TextField
              label={t('inventory.notes')}
              value={formData.notes}
              onChange={handleInputChange('notes')}
              fullWidth
              multiline
              rows={2}
              placeholder={t('inventory.reasonForRemoval')}
            />

            <Box sx={{ p: 2, bgcolor: 'action.hover', borderRadius: 1.5, border: 1, borderColor: 'divider' }}>
              <Typography variant="subtitle2" gutterBottom>
                {t('inventory.operationPreview')}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {t('inventory.removingSummary')}: {formData.quantity} {t('inventory.units')}
              </Typography>
              <Typography variant="body2" color={newQuantity === 0 ? 'error.main' : 'text.secondary'}>
                {t('inventory.newTotal')}: {newQuantity} {t('inventory.units')}
                {newQuantity === 0 && ` (${t('inventory.outOfStock')})`}
              </Typography>
            </Box>
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleClose} disabled={loading}>
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="warning"
            disabled={loading || !isFormValid}
            startIcon={<RemoveIcon />}
          >
            {loading ? t('common.removing') : t('inventory.removeStock')}
          </Button>
        </DialogActions>
      </form>
    </AppDialog>
  );
};

export default RemoveStockDialog;
